
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle 
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wand } from "lucide-react";
import { toast } from "sonner";
import { generateQuizQuestions } from "@/services/modalCampaigns";
import { ModalCampaignQuestionFormData } from "@/types/modalCampaigns";
import { Spinner } from "@/components/ui/spinner";

interface AIQuizGeneratorProps {
  campaignTitle: string;
  campaignContent: string;
  onQuestionsGenerated: (questions: ModalCampaignQuestionFormData[]) => void;
}

const AIQuizGenerator: React.FC<AIQuizGeneratorProps> = ({ 
  campaignTitle,
  campaignContent,
  onQuestionsGenerated 
}) => {
  const [numQuestions, setNumQuestions] = useState<number>(3);
  const [numOptions, setNumOptions] = useState<number>(4);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  
  const handleGenerate = async () => {
    if (!campaignTitle || !campaignContent) {
      toast.error('Vul eerst de titel en inhoud van de campagne in');
      return;
    }
    
    try {
      setIsGenerating(true);
      toast.info('Bezig met genereren van quizvragen...');
      
      const questions = await generateQuizQuestions(
        campaignTitle,
        campaignContent,
        numQuestions,
        numOptions
      );
      
      onQuestionsGenerated(questions);
      toast.success(`${questions.length} vragen succesvol gegenereerd!`);
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Er is een fout opgetreden bij het genereren van quizvragen');
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Wand className="mr-2 h-5 w-5" />
          AI Quiz Generator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Aantal vragen
            </label>
            <Select
              value={String(numQuestions)}
              onValueChange={(value) => setNumQuestions(Number(value))}
              disabled={isGenerating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer aantal" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 5, 10].map(num => (
                  <SelectItem key={num} value={String(num)}>
                    {num} {num === 1 ? 'vraag' : 'vragen'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Antwoordopties per vraag
            </label>
            <Select
              value={String(numOptions)}
              onValueChange={(value) => setNumOptions(Number(value))}
              disabled={isGenerating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecteer aantal" />
              </SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5].map(num => (
                  <SelectItem key={num} value={String(num)}>
                    {num} opties
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={handleGenerate} 
          disabled={isGenerating}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Spinner className="mr-2" />
              Genereren...
            </>
          ) : (
            <>
              <Wand className="mr-2 h-4 w-4" />
              Genereer Quiz Vragen
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AIQuizGenerator;
